/* iter55z-audit round 9 — Trial Conversion Dashboard by tier.
 *
 * Mounted inside Admin → Financials → COGS Analyzer.  Compact
 * widget showing:
 *   - 3 windows (30d / 90d / all-time)
 *   - Overall trial → paid conversion rate per window
 *   - Per-tier converted counts with CAC breakeven days flag
 *
 * Backend: GET /api/admin/financials/trial-conversions
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingUp, Users, DollarSign } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function ratePill(pct) {
  if (pct == null) return null;
  const cls = pct >= 20 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
            : pct >= 10 ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
            : 'bg-neutral-500/15 text-neutral-700 dark:text-neutral-300';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function WindowCard({ title, w, cac }) {
  const [showSrc, setShowSrc] = React.useState(false);
  const sources = w.by_source || [];
  return (
    <div className="rounded-md border border-border bg-card p-3"
         data-testid={`trial-conv-window-${title.toLowerCase().replace(/\W/g, '-')}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
          {title}
        </div>
        {ratePill(w.conversion_rate_pct)}
      </div>
      <div className="mb-3 flex items-baseline gap-2 text-[13px]">
        <span className="font-mono font-semibold text-foreground">{w.converted}</span>
        <span className="text-muted-foreground">converted /</span>
        <span className="font-mono">{w.trials}</span>
        <span className="text-muted-foreground">trials</span>
      </div>
      <div className="space-y-1">
        {w.by_tier.map((t) => (
          <div key={t.tier}
               data-testid={`trial-conv-tier-${title.toLowerCase().replace(/\W/g, '-')}-${t.tier}`}
               className="flex items-center justify-between rounded-sm border border-border/60 px-2 py-1 text-[11.5px]">
            <div className="flex items-center gap-2">
              <span className="w-16 font-semibold uppercase tracking-wider text-muted-foreground">
                {t.tier}
              </span>
              <span className="font-mono text-foreground">{t.converted}</span>
              <span className="text-muted-foreground">×</span>
              <span className="font-mono text-muted-foreground">${t.avg_price_usd?.toFixed(0)}</span>
            </div>
            {t.cac_breakeven_days != null && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold
                  ${t.cac_breakeven_hit
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'bg-neutral-500/10 text-neutral-500'}`}
                title={`Days of MRR at $${t.avg_price_usd} to recover $${cac} CAC`}
                data-testid={`trial-conv-breakeven-${title.toLowerCase().replace(/\W/g, '-')}-${t.tier}`}
              >
                {t.cac_breakeven_hit ? '✓ ' : ''}{t.cac_breakeven_days.toFixed(1)}d CAC
              </span>
            )}
          </div>
        ))}
      </div>

      {/* iter55z-audit round 9 — Per-source breakdown (collapsible). */}
      {sources.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowSrc((s) => !s)}
            data-testid={`trial-conv-source-toggle-${title.toLowerCase().replace(/\W/g, '-')}`}
            className="w-full text-left text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
          >
            {showSrc ? '▾' : '▸'} By source ({sources.length})
          </button>
          {showSrc && (
            <div className="mt-1 space-y-1">
              {sources.map((s) => (
                <div key={s.source}
                     data-testid={`trial-conv-source-${title.toLowerCase().replace(/\W/g, '-')}-${s.source}`}
                     className={`flex items-center justify-between rounded-sm border px-2 py-1 text-[11px]
                       ${s.cac_recovered
                         ? 'border-emerald-500/40 bg-emerald-500/5'
                         : 'border-border/60'}`}>
                  <div className="flex items-center gap-2">
                    <span className="max-w-[9rem] truncate font-semibold text-foreground" title={s.source}>
                      {s.source}
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {s.converted}/{s.trials}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10.5px] text-muted-foreground"
                          title="Expected revenue per trial at Basic sticker">
                      ${s.expected_rev_per_trial_usd.toFixed(2)}/trial
                    </span>
                    <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold
                      ${s.cac_recovered
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                        : 'bg-neutral-500/10 text-neutral-500'}`}>
                      {s.cac_recovered ? '✓ CAC' : `${s.conversion_rate_pct.toFixed(1)}%`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TrialConversionsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/financials/trial-conversions');
      setData(r.data);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-trial-conversions-panel" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" /> Trial → paid conversion by tier
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10.5px] text-muted-foreground"
                  data-testid="trial-conv-cac">
              <DollarSign className="h-3 w-3" /> CAC ${data.cac_usd?.toFixed(2)}
            </span>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </div>
      {!data ? (
        <div className="flex h-24 items-center justify-center text-[12px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <WindowCard title="Last 30 days" w={data.windows['30d']} cac={data.cac_usd} />
          <WindowCard title="Last 90 days" w={data.windows['90d']} cac={data.cac_usd} />
          <WindowCard title="All time"     w={data.windows.all}    cac={data.cac_usd} />
        </div>
      )}
      <div className="mt-3 flex items-start gap-2 rounded-md border border-border/60 bg-secondary/40 p-2 text-[11px] text-muted-foreground">
        <Users className="mt-0.5 h-3 w-3 flex-shrink-0" />
        <span>
          <b>CAC breakeven</b> = days of MRR at the sticker price to recover the trial acquisition cost.
          Lower is better. The ✓ mark indicates at least one conversion at that tier during the window.
        </span>
      </div>
    </section>
  );
}
